<script lang="ts">
  import { SITE_URL, OG_IMAGE } from '$lib/seo';

  /**
   * Open Graph and Twitter card tags for one page.
   *
   * Thirteen of the twenty pages had none at all — including the home page,
   * which meant the site's highest-authority URL shared as a bare link with
   * whatever text a scraper chose to guess. The seven keyword pages had a
   * hand-written set each, and `blog/[slug]` had three of the five.
   *
   * Only `og:*` and `twitter:*` live here. Title and description stay in each
   * page's own `<svelte:head>`: they are the two tags the parity check
   * compares verbatim, and moving twenty of them through a component is a
   * large diff on text that ranks for no benefit the crawler can see. So this
   * takes them as props and restates them for the scrapers, which is the part
   * that was missing.
   *
   * The canonical URL is NOT emitted here — the root layout derives one
   * self-referential canonical for every page, and a second would be worse
   * than none.
   *
   * # Do not let this be the last thing in a page's `<svelte:head>`
   *
   * This component contributes its own `<svelte:head>`, and a component that
   * does so cannot be the final child of the page's head. When it is, the
   * page renders from the server and then loses every style about a second
   * later: hydration throws `hydration_mismatch`, Svelte tears out what it
   * rendered, and the stylesheet link SvelteKit manages in `<head>` goes with
   * it. The console names no file.
   *
   * Bisected across seven probe pages. Title and meta alone are fine; this
   * component last breaks; this component with anything after it is fine.
   * Every page that worked happened to end on a `JsonLd`.
   *
   * Nothing catches it — svelte-check, eslint, the build and the prerenderer
   * all pass, the markup is valid, and parse5 relocates nothing. Put the
   * page's JSON-LD after this and the problem does not arise;
   * `svelte-head-order.test.ts` fails the suite if a page ends on a component.
   */
  let {
    title,
    description,
    path,
    image = OG_IMAGE,
    type = 'website'
  }: {
    /** Usually shorter than the `<title>`; scrapers truncate around 60 chars. */
    title: string;
    description: string;
    /** Site-relative, with a leading slash. `/de` for the home page. */
    path: string;
    image?: string;
    type?: 'website' | 'article';
  } = $props();

  const url = $derived(`${SITE_URL}${path}`);
</script>

<svelte:head>
  <meta property="og:title" content={title} />
  <meta property="og:description" content={description} />
  <meta property="og:type" content={type} />
  <meta property="og:url" content={url} />
  <meta property="og:image" content={image} />
  <meta property="og:site_name" content="AINOVA" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={title} />
  <meta name="twitter:description" content={description} />
  <meta name="twitter:image" content={image} />
</svelte:head>
